// Maths and draft store for the Improvement Suggestions pop-out window. Kept
// free of React and the DOM so the tests can run it directly under node.

// Under the header bar (the button that opens it lives there), tucked into the
// top-right corner with a little breathing room.
export const HEADER_OFFSET = 56;
export const EDGE_MARGIN = 16;
// The drag handle is the window header; this much must always stay on screen
// so a window dragged mostly off the bottom can still be brought back.
export const HANDLE_HEIGHT = 48;

const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

export function initialWindowPosition(viewport, size) {
  return {
    x: Math.max(0, viewport.width - size.width - EDGE_MARGIN),
    y: Math.min(HEADER_OFFSET, Math.max(0, viewport.height - size.height)),
  };
}

// `start` records the pointer and window positions when the drag began; the
// window follows the pointer by the same delta, and is kept inside the viewport.
export function dragWindowTo(start, pointer, size, viewport) {
  return keepOnScreen({
    x: start.window.x + (pointer.x - start.pointer.x),
    y: start.window.y + (pointer.y - start.pointer.y),
  }, size, viewport);
}

// Also run when the zoom changes, since a bigger zoom pushes the same position further out.
export const keepOnScreen = ({ x, y }, size, viewport) => ({
  x: clamp(x, 0, Math.max(0, viewport.width - size.width)),
  y: clamp(y, 0, Math.max(0, viewport.height - HANDLE_HEIGHT)),
});

// The shared Modal moves by its header too, but its overlay lays it out
// centred, so it keeps an offset from there instead of a position. `start` is
// the pointer, the offset and the panel's on-screen rect when the drag began,
// all in the same units. A dialog blocks the page, so it stays wholly on screen.
export const dragDialogTo = ({ pointer: p0, offset, rect }, pointer, viewport) => ({
  x: offset.x + clamp(pointer.x - p0.x, -rect.left, viewport.width - rect.right),
  y: offset.y + clamp(pointer.y - p0.y, -rect.top, viewport.height - rect.bottom),
});

// The window never shrinks below a usable size, and `resizeLimits` caps it at
// the right and bottom screen edges (which matters on a small screen).
export const MIN_WINDOW_SIZE = { width: 320, height: 360 };
export const resizeLimits = (pos, viewport) => ({
  width: Math.max(MIN_WINDOW_SIZE.width, viewport.width - pos.x),
  height: Math.max(MIN_WINDOW_SIZE.height, viewport.height - pos.y),
});

// The resize grip is bottom-left, because the window opens in the top-right
// corner: the right edge stays put and the window grows out towards the page.
// `start` is the pointer, position and size when the resize began.
export function resizeFromBottomLeft(start, pointer, viewport) {
  const right = start.window.x + start.size.width;
  const width = clamp(start.size.width - (pointer.x - start.pointer.x), MIN_WINDOW_SIZE.width, Math.max(MIN_WINDOW_SIZE.width, right));
  const maxHeight = Math.max(MIN_WINDOW_SIZE.height, viewport.height - start.window.y);
  const height = clamp(start.size.height + (pointer.y - start.pointer.y), MIN_WINDOW_SIZE.height, maxHeight);
  return { pos: { x: right - width, y: start.window.y }, size: { width, height } };
}

// The window takes the header's S / M / L density zoom. Its left/top are then
// in zoomed units, so screen pixels (pointer, viewport) are divided down first.
export const unzoom = (o, zoom) => Object.fromEntries(Object.entries(o).map(([k, v]) => [k, v / zoom]));

// The half-written suggestion outlives the window. Module state rather than
// browser storage: it should last the session, not reappear next week, and it
// must not leak between people sharing a machine.
const EMPTY_DRAFT = { title: '', description: '', category: '', priority: 'medium' };
let draft = { ...EMPTY_DRAFT };

// Somebody who closed the window mid-sentence wants their sentence back, not
// the list of past suggestions.
export const initialTab = (d) => (d.title.trim() || d.description.trim()) ? 'new' : 'mine';

export const suggestionDraft = {
  load: () => ({ ...draft }),
  save: (next) => { draft = { ...EMPTY_DRAFT, ...next }; },
  clear: () => { draft = { ...EMPTY_DRAFT }; },
};

// Archived suggestions stay in the payload (the toggle needs to know how many
// there are) but leave the list until asked for.
export const visibleSuggestions = (suggestions, showArchived) =>
  showArchived ? suggestions : suggestions.filter(s => !s.archived_at);

// Only finished business can be archived. An open or in-progress ticket hidden
// from its own raiser would look lost, and one awaiting their test still needs
// an answer from them first.
export const canArchive = (s) =>
  (s.status === 'done' || s.status === 'declined') && s.ai_proposal_status !== 'awaiting_testing';

// List card tint: a card takes its status badge's colour; archived cards stay plain.
const SUGGESTION_CARD_TINT = {
  open: 'bg-amber-50 border-amber-200',
  'in-progress': 'bg-blue-50 border-blue-200',
  done: 'bg-green-50 border-green-200',
  declined: 'bg-red-50 border-red-200',
};
export const suggestionStatusTint = (status) => SUGGESTION_CARD_TINT[status] || 'bg-white border-gray-200';
export const suggestionCardTint = (s) => s.archived_at ? 'bg-white border-gray-200' : suggestionStatusTint(s.status);
